/*************************************************************************
* ADOBE CONFIDENTIAL
* ___________________
*
*  Copyright 2024 Adobe
*  All Rights Reserved.
*
* NOTICE:  All information contained herein is, and remains
* the property of Adobe and its suppliers, if any. The intellectual
* and technical concepts contained herein are proprietary to Adobe
* and its suppliers and are protected by all applicable intellectual
* property laws, including trade secret and copyright laws.
* Dissemination of this information or reproduction of this material
* is strictly forbidden unless prior written permission is obtained
* from Adobe.
**************************************************************************/
/* eslint no-empty: 0 */

/**
 * Suppresses a DOM SecurityError if it occurs.
 * @param {Error} e - The error to check and possibly suppress.
 */
const suppressDOMSecurityExceptionIfAny = e => {
  if (!(e instanceof DOMException && e.name === 'SecurityError')) {
    throw e;
  }
};

export interface ISessionStorage extends Storage {
}

export interface ILocalStorage extends Storage {
}

/**
 * Storage class that mimics the behavior of the Storage object using a custom source.
 * @class
 * @extends {Storage}
 */
class SafeStorage implements Storage {
  /**
   * Creates an instance of SafeStorage.
   * @param {Storage} source - The storage source to use.
   */
  constructor(private source: Storage) {}

  /**
   * Adds or updates a key-value pair in the storage.
   * @param {string} key - The key to set.
   * @param {string} value - The value to associate with the key.
   */
  setItem(key: string, value: string) {
    try {
      this.source.setItem(key, value);
    } catch (e) {
      suppressDOMSecurityExceptionIfAny(e);
    }
  }

  /**
   * Retrieves the value associated with the specified key from the storage.
   * @param {string} key - The key whose associated value is to be retrieved.
   * @returns {string|null} The value associated with the given key, or null if the key does not exist.
   */
  getItem(key: string): string | null {
    let value = null;
    try {
      value = this.source.getItem(key);
    } catch (e) {
      suppressDOMSecurityExceptionIfAny(e);
    }
    return value;
  }

  /**
   * Removes the key-value pair associated with the specified key from the storage.
   * @param {string} key - The key of the pair to be removed.
   */
  removeItem(key: string) {
    try {
      this.source.removeItem(key);
    } catch (e) {
      suppressDOMSecurityExceptionIfAny(e);
    }
  }

  /**
   * Removes all key-value pairs from the storage.
   */
  clear() {
    try {
      this.source.clear();
    } catch (e) {
      suppressDOMSecurityExceptionIfAny(e);
    }
  }

  /**
   * Retrieves the key at the specified index in the storage.
   * @param {number} index - The zero-based index of the key to retrieve.
   * @returns {string|null} The key at the specified index, or null if the index is out of range.
   */
  key(index: number): string | null {
    let key = null;
    try {
      key = this.source.key(index);
    } catch (e) {
      suppressDOMSecurityExceptionIfAny(e);
    }
    return key;
  }

  /**
   * Retrieves the number of key-value pairs in the storage.
   * @type {number}
   */
  get length() {
    let length = 0;
    try {
      length = this.source.length;
    } catch (e) {
      suppressDOMSecurityExceptionIfAny(e);
    }
    return length;
  }
}

export default SafeStorage;
